import { useLayoutEffect, useRef } from 'react'
import { useMatch } from 'react-router-dom'
import gsap from 'gsap'
import { SITE, whatsappHref } from '../../data/site'
import { EASE, prefersReducedMotion } from '../../animations/gsapAnimations'
import WhatsAppIcon from '../ui/WhatsAppIcon'

/* The one action that is always a thumb — or a cursor — away.

   It used to be a two-button bar pinned across the bottom of a phone, with
   the lease calculator taking the wide half. That bar cost a strip of every
   page it appeared on and only existed on mobile, while the enquiry it was
   competing with arrives on WhatsApp either way. So it is a single mark in
   the bottom-right corner now, the same on a desk and in a hand: WhatsApp's
   own glyph on WhatsApp's own green, which is understood before it is read.

   `--color-mint` is #25d366 — the brand green itself, already in the palette
   for exactly this — so the button belongs to the page without pretending to
   be something other than what it opens. */
function WhatsAppFab() {
  /* A vehicle page keeps its own sticky price bar across the bottom on a
     phone. The mark lifts clear of it rather than sitting on top of the
     "Book now" it would otherwise cover; on a desktop that bar is gone and
     the corner is the corner. */
  const onVehiclePage = useMatch('/cars/:id') !== null
  const ref = useRef<HTMLAnchorElement>(null)

  useLayoutEffect(() => {
    const button = ref.current
    if (!button) return
    if (prefersReducedMotion()) {
      gsap.set(button, { scale: 1, opacity: 1 })
      return
    }

    /* A short beat after the page settles, so it arrives as an offer rather
       than as part of the layout the visitor is still reading. */
    const tween = gsap.fromTo(
      button,
      { scale: 0.6, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.5, delay: 0.6, ease: EASE },
    )

    return () => {
      tween.kill()
    }
  }, [])

  return (
    <a
      ref={ref}
      href={whatsappHref(
        `Hi ${SITE.name} ${SITE.suffix}, I'd like to ask about a car.`,
        SITE.salesWhatsapp,
      )}
      target="_blank"
      rel="noreferrer"
      aria-label="Message us on WhatsApp"
      title="Message us on WhatsApp"
      className={`fixed right-4 z-40 grid size-14 place-items-center rounded-full bg-mint text-white shadow-pop ring-1 ring-white/15 transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-action focus-visible:scale-105 md:right-6 md:bottom-6 ${
        onVehiclePage ? 'bottom-24' : 'bottom-5'
      }`}
    >
      <WhatsAppIcon className="size-7" />
    </a>
  )
}

export default WhatsAppFab
